import type { Location, CreateLocationData, UpdateLocationData, LocationStats, LocationShifts } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003/api';

// Simulador de delay para desarrollo
const simulateDelay = (ms: number = 500) => new Promise(resolve => setTimeout(resolve, ms));

export const locationsService = {
  async getLocations(): Promise<Location[]> {
    await simulateDelay();
    
    try {
      const response = await fetch(`${API_BASE_URL}/locations/all`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Error al obtener sucursales');
      }
      
      const data = await response.json();
      return data.data || [];
    } catch (error) {
      console.error('Error fetching locations:', error);
      // Retornar datos mock si falla la API
      return getMockLocations();
    }
  },

  async getLocationById(id: string): Promise<Location | null> {
    await simulateDelay();
    
    try {
      const response = await fetch(`${API_BASE_URL}/locations/${id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Sucursal no encontrada');
      }
      
      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error fetching location:', error);
      const mockLocations = getMockLocations();
      return mockLocations.find(loc => loc.id === id) || null;
    }
  },

  async createLocation(locationData: CreateLocationData): Promise<Location> {
    await simulateDelay();
    
    try {
      const response = await fetch(`${API_BASE_URL}/locations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: JSON.stringify(locationData),
      });
      
      if (!response.ok) {
        // Verificar si es error de token
        if (response.status === 401) {
          throw new Error('Su sesión ha expirado. Por favor, inicie sesión nuevamente.');
        }
        
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Error al crear sucursal');
      }
      
      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error creating location:', error);
      // Propagar el error en lugar de devolver datos mock
      throw error instanceof Error ? error : new Error('Error al crear sucursal');
    }
  },

  async updateLocation(locationData: UpdateLocationData): Promise<Location> {
    await simulateDelay();
    
    try {
      const response = await fetch(`${API_BASE_URL}/locations/${locationData.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: JSON.stringify(locationData),
      });
      
      if (!response.ok) {
        // Verificar si es error de token
        if (response.status === 401) {
          throw new Error('Su sesión ha expirado. Por favor, inicie sesión nuevamente.');
        }
        
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Error al actualizar sucursal');
      }
      
      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error updating location:', error);
      
      // Si es error de autenticación, no usar datos mock
      if (error instanceof Error && error.message.includes('sesión ha expirado')) {
        throw error;
      }
      
      // Para otros errores, continuar con el error original
      throw error;
    }
  },

  async deleteLocation(id: string): Promise<void> {
    await simulateDelay();
    
    try {
      const response = await fetch(`${API_BASE_URL}/locations/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      
      if (!response.ok) {
        // Verificar si es error de token
        if (response.status === 401) {
          throw new Error('Su sesión ha expirado. Por favor, inicie sesión nuevamente.');
        }
        
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Error al eliminar sucursal');
      }
    } catch (error) {
      console.error('Error deleting location:', error);
      
      // Si es error de autenticación, no usar datos mock
      if (error instanceof Error && error.message.includes('sesión ha expirado')) {
        throw error;
      }
      
      throw error;
    }
  },

  async getLocationStats(): Promise<LocationStats> {
    await simulateDelay();
    
    try {
      const response = await fetch(`${API_BASE_URL}/locations/stats`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Error al obtener estadísticas');
      }
      
      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error fetching stats:', error);
      // Retornar estadísticas mock
      return {
        totalLocations: 3,
        activosCount: 2,
        inactivosCount: 1,
        mantenimientoCount: 0,
        ventasTotales: 125000,
        clientesTotales: 1250,
      };
    }
  },

  async toggleLocationStatus(id: string, newStatus: Location['estado']): Promise<Location> {
    await simulateDelay();

    try {
      const response = await fetch(`${API_BASE_URL}/locations/${id}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: JSON.stringify({ estado: newStatus }),
      });

      if (!response.ok) {
        // Verificar si es error de token
        if (response.status === 401) {
          throw new Error('Su sesión ha expirado. Por favor, inicie sesión nuevamente.');
        }

        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Error al cambiar estado');
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error toggling status:', error);

      // Si es error de autenticación, no usar datos mock
      if (error instanceof Error && error.message.includes('sesión ha expirado')) {
        throw error;
      }

      throw error;
    }
  },

  async getLocationShifts(locationId: string | number, date?: string): Promise<LocationShifts> {
    await simulateDelay();

    try {
      const queryParams = date ? `?date=${date}` : '';
      const response = await fetch(`${API_BASE_URL}/locations/${locationId}/shifts${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });

      if (!response.ok) {
        throw new Error('Error al obtener turnos');
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error fetching location shifts:', error);
      // Retornar datos mock en caso de error
      return getMockLocationShifts(locationId);
    }
  }
};

// Función auxiliar para datos mock
function getMockLocations(): Location[] {
  return [
    {
      id: 'loc-1',
      nombre: 'Macartur Centro',
      direccion: 'Jr. de la Unión 123, Cercado de Lima',
      telefono: '+51 1 234-5678',
      email: 'centro@macartur.pe',
      gerente: 'María González',
      estado: 'ACTIVO',
      tipoLocal: 'PRINCIPAL',
      horarios: {
        lunes: { abierto: true, horaApertura: '07:00', horaCierre: '22:00' },
        martes: { abierto: true, horaApertura: '07:00', horaCierre: '22:00' },
        miercoles: { abierto: true, horaApertura: '07:00', horaCierre: '22:00' },
        jueves: { abierto: true, horaApertura: '07:00', horaCierre: '22:00' },
        viernes: { abierto: true, horaApertura: '07:00', horaCierre: '23:00' },
        sabado: { abierto: true, horaApertura: '08:00', horaCierre: '23:00' },
        domingo: { abierto: true, horaApertura: '08:00', horaCierre: '21:00' },
      },
      capacidad: 80,
      fechaApertura: '2015-03-15',
      companyId: 'comp-1',
      configuracion: {
        aceptaEfectivo: true,
        aceptaTarjeta: true,
        aceptaYape: true,
        tieneDelivery: true,
        tieneRecojo: true,
      },
      metricas: {
        ventasUltimoMes: 65000,
        clientesActivos: 850,
        empleadosAsignados: 12,
      },
    },
    {
      id: 'loc-2',
      nombre: 'Macartur Miraflores',
      direccion: 'Av. Larco 456, Miraflores',
      telefono: '+51 1 987-6543',
      email: 'miraflores@macartur.pe',
      gerente: 'Carlos Mendoza',
      estado: 'ACTIVO',
      tipoLocal: 'SUCURSAL',
      horarios: {
        lunes: { abierto: true, horaApertura: '08:00', horaCierre: '22:00' },
        martes: { abierto: true, horaApertura: '08:00', horaCierre: '22:00' },
        miercoles: { abierto: true, horaApertura: '08:00', horaCierre: '22:00' },
        jueves: { abierto: true, horaApertura: '08:00', horaCierre: '22:00' },
        viernes: { abierto: true, horaApertura: '08:00', horaCierre: '23:00' },
        sabado: { abierto: true, horaApertura: '09:00', horaCierre: '23:00' },
        domingo: { abierto: true, horaApertura: '09:00', horaCierre: '21:00' },
      },
      capacidad: 60,
      fechaApertura: '2018-07-20',
      companyId: 'comp-1',
      configuracion: {
        aceptaEfectivo: true,
        aceptaTarjeta: true,
        aceptaYape: true,
        tieneDelivery: true,
        tieneRecojo: true,
      },
      metricas: {
        ventasUltimoMes: 45000,
        clientesActivos: 620,
        empleadosAsignados: 8,
      },
    },
    {
      id: 'loc-3',
      nombre: 'Macartur San Isidro',
      direccion: 'Av. Salaverry 789, San Isidro',
      telefono: '+51 1 555-0123',
      email: 'sanisidro@macartur.pe',
      gerente: 'Ana Torres',
      estado: 'MANTENIMIENTO',
      tipoLocal: 'SUCURSAL',
      horarios: {
        lunes: { abierto: false, horaApertura: '08:00', horaCierre: '21:00' },
        martes: { abierto: false, horaApertura: '08:00', horaCierre: '21:00' },
        miercoles: { abierto: false, horaApertura: '08:00', horaCierre: '21:00' },
        jueves: { abierto: false, horaApertura: '08:00', horaCierre: '21:00' },
        viernes: { abierto: false, horaApertura: '08:00', horaCierre: '22:00' },
        sabado: { abierto: false, horaApertura: '09:00', horaCierre: '22:00' },
        domingo: { abierto: false, horaApertura: '09:00', horaCierre: '20:00' },
      },
      capacidad: 45,
      fechaApertura: '2020-11-10',
      companyId: 'comp-1',
      configuracion: {
        aceptaEfectivo: true,
        aceptaTarjeta: true,
        aceptaYape: true,
        tieneDelivery: false,
        tieneRecojo: true,
      },
      metricas: {
        ventasUltimoMes: 0,
        clientesActivos: 320,
        empleadosAsignados: 5,
      },
    },
  ];
}

// Función auxiliar para datos mock de turnos
function getMockLocationShifts(locationId: string | number): LocationShifts {
  const today = new Date().toISOString().split('T')[0];

  // Convertir locationId a número
  let numericId: number;
  if (typeof locationId === 'string') {
    if (locationId.startsWith('loc-')) {
      numericId = parseInt(locationId.replace('loc-', '')) || 1;
    } else {
      numericId = parseInt(locationId) || 1;
    }
  } else {
    numericId = locationId || 1;
  }

  // Simular diferentes configuraciones de turnos según la ubicación
  if (numericId === 1) {
    // Macartur Centro - 2 turnos
    return {
      location_id: numericId,
      date: today,
      is_open: true,
      shifts: [
        {
          type: 'morning',
          name: 'Mañana',
          start_time: '07:00',
          end_time: '15:00'
        },
        {
          type: 'afternoon',
          name: 'Tarde',
          start_time: '15:00',
          end_time: '22:00'
        }
      ],
      shift_type: 'split'
    };
  } else {
    // Otras ubicaciones - turno único
    return {
      location_id: numericId,
      date: today,
      is_open: true,
      shifts: [
        {
          type: 'morning',
          name: 'Mañana',
          start_time: '08:00',
          end_time: '22:00'
        }
      ],
      shift_type: 'single'
    };
  }
}